"use client";
import type {
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";
import { OfflineImage } from "../../shared/offline-image";

import {
  Download,
  Eye,
  FileText,
  Loader2,
  Plus,
  Save,
  Trash2,
  TrendingUp,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
  useVersionAwareWorks,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import {
  createWork,
  deleteWork,
  fetchWorks,
  updateWork,
} from "../../../redux/slices/workSlice.js";
import { punchListConstructionProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import { Label } from "../../ui/label.jsx";
import { LoadingIndicator } from "../../ui/loading-indicator";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "../shared/image-capture-picker";
import { LocalSectionImportControl } from "../shared/local-section-import-control";

interface FileAttachment extends AuditFileMetadata {
  id: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  uploadedDate: string;
  description?: string;
}

interface ConstructionProgressItem {
  id: string;
  name: string;
  category:
    | "civil"
    | "mechanical"
    | "electrical"
    | "dno"
    | "foundation"
    | "commissioning";
  plannedStart: string;
  plannedEnd: string;
  actualStart: string;
  actualEnd: string;
  percentComplete: number;
  status: "pending" | "in-progress" | "completed" | "delayed";
  bottlenecks: string;
  attachments: FileAttachment[];
}

interface ConstructionProgressData {
  phases: ConstructionProgressItem[];
  overallSummary: string;
  comments: string;
}

interface WorkItem {
  id: string;
  _id?: string;
  name?: string;
  category?: string;
  plannedStart?: string;
  plannedEnd?: string;
  actualStart?: string;
  actualEnd?: string;
  percentComplete?: number;
  status?: string;
  bottlenecks?: string;
  comments?: string;
}

const initialData: ConstructionProgressData = {
  phases: [],
  overallSummary: "",
  comments: "",
};

export const ConstructionProgress = memo(function ConstructionProgress({
  siteId,
  templateId,
  reportId,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: {
  siteId: string;
  templateId: string | null;
  reportId: string | null;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  readOnly?: boolean;
  isViewingHistoricalVersion?: boolean;
  versionData?: any;
  previousCompletedReports?: any[];
}) {
  const { notify } = useNotification();

  const [formData, setFormData] =
    useState<ConstructionProgressData>(initialData);
  const formDataRef = useLatest(formData);
  const [activityData, setActivityData] = useState<
    Record<string, Partial<WorkItem>>
  >({});
  const [activeTab, setActiveTab] = useState<string>("all");
  const [selectedFile, setSelectedFile] = useState<FileAttachment | null>(null);

  const [uploadingWorkImages, setUploadingWorkImages] = useState<
    Record<string, boolean>
  >({});
  const [workProgressData, setWorkProgressData] = useState([]);

  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  const dispatch = useAppDispatch();

  const currentSiteData = useVersionAwareSiteData(readOnly);
  const currentWorksData = useVersionAwareWorks(readOnly);

  useEffect(() => {
    dispatch(fetchWorks({ siteId, templateId, reportId }) as any);
    dispatch(fetchFiles({ siteId, reportId }) as any);
  }, [dispatch, siteId, templateId, reportId]);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power).
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:construction-progress` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (
      JSON.stringify(currentSiteData?.punchList?.constructionProgress || {}) ===
      JSON.stringify(formData)
    ) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    const progress = punchListConstructionProgress(
      workProgressData,
      formData?.overallSummary,
      files,
    );

    setSessionProgress((prev) => ({
      ...prev,
      [templateId || "punch-list"]: {
        ...(prev[templateId || "punch-list"] || {}),
        "construction-progress": progress,
      },
    }));
  }, [formData, workProgressData, templateId, files, setSessionProgress]);

  const handleSaveWork = useCallback(
    async (data: typeof activityData) => {
      try {
        const importedWorkEntries = workProgressData
          .filter((work: any) => work?._sourceId && !work?._id)
          .map((work: any) => [work.id, work] as [string, Partial<WorkItem>]);
        const workEntries = [
          ...Object.entries(data),
          ...importedWorkEntries.filter(([workId]) => !data[workId]),
        ];

        const mutationResult1 = await Promise.all(
          workEntries.map(([workId, pendingWorkData]) => {
            const existingWork = workProgressData.find(
              (work: any) => work?._id === workId || work?.id === workId,
            );
            const { _id, ...existingWorkData } = existingWork || {};
            const isImportedWork =
              String(workId).startsWith("imported-") || !existingWork?._id;
            const payload = {
              ...(existingWorkData || {}),
              ...pendingWorkData,
              siteId,
              templateId,
              workCategory: "construction-progress",
            };

            return isImportedWork
              ? requireMutationSuccess(dispatch(createWork(payload as any)))
              : dispatch(updateWork({ workId, workData: payload })).unwrap();
          }),
        );
        const config = notificationHelpers.formSaveSuccess("Work Data", [
          mutationResult1,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Work Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, notify, workProgressData, siteId, templateId],
  );

  const handleSaveForm = useCallback(
    async (data: typeof formData) => {
      if (!siteId) return;

      try {
        const mutationResult2 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: {
                reportId,
                punchList: {
                  constructionProgress: {
                    overallSummary: data.overallSummary || "",
                    comments: data.comments || "",
                  },
                },
              },
            } as any),
          ),
        );
        const config = notificationHelpers.formSaveSuccess("Form Data", [
          mutationResult2,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Form Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, siteId, reportId, notify],
  );

  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult3 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult3],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  const {
    isSaving: isWorkSaving,
    lastSaved: lastSavedWork,
    hasUnsavedChanges: hasWorkUnsavedChanges,
    saveError: workSaveError,
    forceSave: forceWorkSave,
  } = useDebouncedSave(activityData, {
    delay: 5000,
    onSave: handleSaveWork,
    onError: (error) => console.error("Work save error:", error),
    readOnly,
  });

  const {
    resetBaseline: formResetBaseLine,
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
  } = useDebouncedSave(formData, {
    delay: 5000,
    onSave: handleSaveForm,
    onError: (error) => console.error("Form save error:", error),
    readOnly,
  });

  useEffect(() => {
    const constructionProgressData =
      currentSiteData?.punchList?.constructionProgress || {};

    const newFormData = { ...formDataRef.current, ...constructionProgressData };
    formResetBaseLine(newFormData);
    setFormData(newFormData);
    setWorkProgressData(
      currentWorksData?.filter(
        (w: any) => w.workCategory === "construction-progress",
      ) || [],
    );
  }, [
    currentSiteData,
    currentWorksData,
    templateId,
    formResetBaseLine,
    formDataRef,
  ]);

  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  const isSaving = isFormSaving || isWorkSaving || isImageSaving;
  const hasUnsavedChanges =
    hasFormUnsavedChanges || hasWorkUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || workSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    const dates = [lastSavedForm, lastSavedImage, lastSavedWork].filter(
      Boolean,
    );
    if (dates.length === 0) return null;
    return new Date(Math.max(...dates.map((date) => new Date(date).getTime())));
  }, [lastSavedForm, lastSavedWork, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceWorkSave();
    forceImageSave();
  }, [forceFormSave, forceWorkSave, forceImageSave]);

  const handleFileUpload = (
    files: FileList | File[] | null,
    filterData: any,
    entityId?: string,
  ) => {
    if (!files) return;

    const uploadKey = JSON.stringify(filterData);
    setUploadingWorkImages((prev) => ({ ...prev, [uploadKey]: true }));

    const fileArray = Array.from(files);

    requireMutationSuccess(
      dispatch(
        uploadMultipleFiles({
          files: fileArray,
          metadata: {
            siteId: siteId,
            entityType: `construction-progress`,
            entityId,
            reportId,
          },
          // filterData,
        }),
      ),
    )
      .then((mutationOutcome) => {
        fileArray.forEach((file) => {
          const config = notificationHelpers.imageUploadSuccess(
            file.name,
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        });
      })
      .catch((error) => {
        const config = notificationHelpers.imageUploadError(
          fileArray.length === 1
            ? fileArray[0].name
            : `${fileArray.length} files`,
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
      })
      .finally(() => {
        setUploadingWorkImages((prev) => ({ ...prev, [uploadKey]: false }));
      });
  };

  const removeFile = (fileId: string) => {
    requireMutationSuccess(dispatch(deleteFile(fileId) as any))
      .then((mutationOutcome) => {
        const config = notificationHelpers.imageDeleteSuccess(
          "Image",
          mutationOutcome,
        );
        notify(config.message, config.type, config.options);
      })
      .catch((error) => {
        const config = notificationHelpers.imageDeleteError(
          "Image",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
      });
  };

  const updateImageDescription = useCallback(
    (fileId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [fileId]: description }));
    },
    [setImageDescriptions],
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const getFilesByEntity = (filterData: any, entityId?: string) => {
    return files.filter(
      (file: any) =>
        file?.relatedTo?.entityType === `construction-progress` &&
        (!entityId || file?.relatedTo?.entityId === entityId),
      //  &&
      // file?.relatedTo?.filterData &&
      // JSON.stringify(file?.relatedTo?.filterData) ===
      //   JSON.stringify(filterData),
    );
  };

  const addActivity = async (category: string = "civil") => {
    try {
      await requireMutationSuccess(
        dispatch(
          createWork({
            siteId,
            templateId,
            name: "",
            category,
            workType: category,
            workCategory: "construction-progress",
            status: "pending",
            percentComplete: 0,
          } as any),
        ),
      )
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionCreate(
            "Construction Progress Entry",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.actionCreateError(
            "Progress Entry",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      const config = notificationHelpers.actionCreateError(
        "Progress Entry",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
    }
  };

  const removeActivity = async (id: string) => {
    try {
      await requireMutationSuccess(dispatch(deleteWork(id) as any))
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionDelete(
            "Progress Entry",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.actionDeleteError(
            "Progress Entry",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      const config = notificationHelpers.actionDeleteError(
        "Progress Entry",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
    }
  };

  const updateActivity = (
    id: string,
    field: keyof ConstructionProgressItem | string,
    value: any,
  ) => {
    setWorkProgressData((prev) =>
      prev.map((work) =>
        work.id === id || work._id === id ? { ...work, [field]: value } : work,
      ),
    );
    setActivityData((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const getWorkValue = <K extends keyof WorkItem>(
    item: WorkItem,
    field: K,
  ): WorkItem[K] =>
    field in (activityData[item.id] || {})
      ? (activityData[item.id][field] as WorkItem[K])
      : item[field];

  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);
    return uploadingWorkImages[uploadKey] || false;
  };
  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };
  const renderFileAttachments = (filterData: any, entityId?: string) => {
    const entityFiles = getFilesByEntity(filterData, entityId);
    const isUploading = isUploadingForSection(filterData);

    return (
      <div className="space-y-3">
        {isUploading && (
          <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
              <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
            </div>
          </div>
        )}
        <Label className="text-sm font-medium flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Supporting Documents & Photos
        </Label>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {entityFiles.map((file: any) => (
            <div key={file.id} className="relative group">
              {file?.fileType?.startsWith("image/") ? (
                <div className="aspect-video bg-slate-100 rounded overflow-hidden dark:bg-muted">
                  <OfflineImage
                    fileId={file?._id || file?.id || file?._clientId}
                    src={file.fileUrl}
                    alt={file.originalName}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-slate-100 rounded flex items-center justify-center dark:bg-muted">
                  <FileText className="w-8 h-8 text-slate-400 dark:text-muted-foreground" />
                </div>
              )}

              <div className="absolute top-1 right-1 flex gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                  onClick={() => setSelectedFile(file)}
                >
                  <Eye className="w-3 h-3" />
                </Button>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                    onClick={() => removeFile(file.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>

              <div className="mt-1 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium text-slate-700 truncate dark:text-foreground">
                    {file.originalName}
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {formatFileSize(file.fileSize)}
                  </Badge>
                </div>
                {!readOnly && (
                  <input
                    type="text"
                    placeholder="Add description..."
                    value={
                      imageDescriptions[file.id] || file.fileDescription || ""
                    }
                    onChange={(e) =>
                      updateImageDescription(file.id, e.target.value)
                    }
                    className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-border dark:focus:ring-ring"
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {!readOnly && (
          <ImageCapturePicker
            buttonText="Click to upload photos"
            isUploading={isUploading}
            className="py-3 border-2 border-dashed border-slate-300 hover:border-slate-400 dark:border-border"
            onConfirm={(selectedFiles) =>
              handleFileUpload(selectedFiles, filterData, entityId)
            }
          />
        )}
      </div>
    );
  };

  return (
    <Card className="w-full">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="construction-progress"
          siteId={siteId}
          reportId={reportId || undefined}
          readOnly={readOnly}
          setFormData={setFormData}
          workProgressData={workProgressData}
          setWorkProgressData={setWorkProgressData}
        />
      )}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Construction Progress
          </CardTitle>
          {!readOnly && (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => addActivity("civil")}
                disabled={readOnly}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Civil
              </Button>
              <Button
                size="sm"
                onClick={() => addActivity("mechanical")}
                disabled={readOnly}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Mechanical
              </Button>
              <Button
                size="sm"
                onClick={() => addActivity("electrical")}
                disabled={readOnly}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Electrical
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all">
              All - {workProgressData.length || 0}
            </TabsTrigger>
            <TabsTrigger value="civil">
              Civil -{" "}
              {workProgressData.filter((work: any) => work.workType === "civil")
                .length || 0}
            </TabsTrigger>
            <TabsTrigger value="mechanical">
              Mechanical -{" "}
              {workProgressData.filter(
                (work: any) => work.workType === "mechanical",
              ).length || 0}
            </TabsTrigger>
            <TabsTrigger value="electrical">
              Electrical -{" "}
              {workProgressData.filter(
                (work: any) => work.workType === "electrical",
              ).length || 0}
            </TabsTrigger>
          </TabsList>

          <div className="space-y-4 mt-4">
            {workProgressData
              .filter(
                (work: any) =>
                  activeTab === "all" ||
                  work.workType === activeTab ||
                  work.category === activeTab,
              )
              .map((work: any) => (
                <Card key={work.id || work._id} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base">
                            {getWorkValue(work, "name") || "Untitled Phase"}
                          </CardTitle>
                          <Badge variant="secondary">
                            {getWorkValue(work, "category") || "civil"}
                          </Badge>
                        </div>
                      </div>
                      {!readOnly && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => removeActivity(work.id || work._id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <OptimizedInput
                        label="Phase Name"
                        value={getWorkValue(work, "name") || ""}
                        onChange={(e) =>
                          updateActivity(work.id || work._id, "name", e)
                        }
                        placeholder="Enter phase name"
                        readOnly={readOnly}
                      />
                      <OptimizedSelect
                        label="Status"
                        value={getWorkValue(work, "status") || "pending"}
                        onChange={(value) =>
                          updateActivity(work.id || work._id, "status", value)
                        }
                        options={[
                          { value: "pending", label: "Pending" },
                          { value: "in-progress", label: "In Progress" },
                          { value: "completed", label: "Completed" },
                          // { value: "delayed", label: "Delayed" },
                        ]}
                        disabled={readOnly}
                        required
                      />
                      {/* <div>
                    <Label className="text-sm font-medium">Planned Start</Label>
                    <OptimizedInput
                      type="date"
                      value={getWorkValue(work, "plannedStart") || ""}
                      onChange={(e) => updateActivity(work.id || work._id, "plannedStart", e)}
                      readOnly={readOnly}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Planned End</Label>
                    <OptimizedInput
                      type="date"
                      value={getWorkValue(work, "plannedEnd") || ""}
                      onChange={(e) => updateActivity(work.id || work._id, "plannedEnd", e)}
                      readOnly={readOnly}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Actual Start</Label>
                    <OptimizedInput
                      type="date"
                      value={getWorkValue(work, "actualStart") || ""}
                      onChange={(e) => updateActivity(work.id || work._id, "actualStart", e)}
                      readOnly={readOnly}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Actual End</Label>
                    <OptimizedInput
                      type="date"
                      value={getWorkValue(work, "actualEnd") || ""}
                      onChange={(e) => updateActivity(work.id || work._id, "actualEnd", e)}
                      readOnly={readOnly}
                    />
                  </div> */}
                    </div>

                    {/* <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">
                          Percent Complete
                        </Label>
                        <span className="text-sm font-bold text-blue-600">
                          {getWorkValue(work, "percentComplete") || 0}%
                        </span>
                      </div>
                      <div className="space-y-2">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={getWorkValue(work, "percentComplete") || 0}
                          onChange={(e) =>
                            updateActivity(
                              work.id || work._id,
                              "percentComplete",
                              parseInt(e.target.value),
                            )
                          }
                          disabled={readOnly}
                          className="w-full"
                        />
                        <Progress value={getWorkValue(work, "percentComplete") || 0} className="h-2" />
                      </div>
                    </div> */}

                    <OptimizedTextarea
                      label="Bottlenecks / Issues"
                      value={getWorkValue(work, "bottlenecks") || ""}
                      onChange={(e) =>
                        updateActivity(work.id || work._id, "bottlenecks", e)
                      }
                      placeholder="Describe any bottlenecks or issues encountered"
                      readOnly={readOnly}
                    />

                    {/* <OptimizedTextarea
                      label="Description"
                      value={getWorkValue(work, "description") || ""}
                      onChange={(e) =>
                        updateActivity(work.id || work._id, "description", e)
                      }
                      placeholder="Add description about this construction phase"
                      readOnly={readOnly}
                    /> */}

                    <OptimizedTextarea
                      label="Comments"
                      value={getWorkValue(work, "comments") || ""}
                      onChange={(e) =>
                        updateActivity(work.id || work._id, "comments", e)
                      }
                      placeholder="Add comments about this construction phase"
                      readOnly={readOnly}
                    />

                    {renderFileAttachments(
                      { [work.id || work._id]: true },
                      work.id || work._id,
                    )}
                  </CardContent>
                </Card>
              ))}
          </div>
        </Tabs>

        <div className="border-t pt-6 space-y-4">
          <OptimizedTextarea
            label="Overall Summary"
            value={formData.overallSummary || ""}
            onChange={(e) => setFormData({ ...formData, overallSummary: e })}
            placeholder="Provide an overall summary of construction progress"
            readOnly={readOnly}
            rows={3}
          />

          <OptimizedTextarea
            label="Comments"
            value={formData.comments || ""}
            onChange={(e) => setFormData({ ...formData, comments: e })}
            placeholder="Add any additional comments"
            readOnly={readOnly}
            rows={2}
          />
        </div>
      </CardContent>

      {/* File Preview Modal */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-auto dark:bg-card">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedFile.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFile(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              {selectedFile?.fileType?.startsWith("image/") ? (
                <OfflineImage
                  fileId={
                    selectedFile?._id ||
                    selectedFile?.id ||
                    selectedFile?._clientId
                  }
                  src={selectedFile?.fileUrl || "/placeholder.svg"}
                  alt={selectedFile?.originalName}
                  className="max-w-full max-h-96 mx-auto"
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4 dark:text-muted-foreground" />
                  <p className="text-slate-600 dark:text-muted-foreground">
                    Preview not available for this file type
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => window.open(selectedFile.fileUrl, "_blank")}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download File
                  </Button>
                </div>
              )}
              <div className="mt-4 text-sm text-slate-500 dark:text-muted-foreground">
                <p>Size: {formatFileSize(selectedFile.fileSize)}</p>
                <p>
                  Uploaded:{" "}
                  {new Date(selectedFile.uploadDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
});
